import type { Task } from '../../types';
import { sortReviewTasks } from './reviewSort';

export const UNASSIGNED_REVIEW_BROWSE_KIND = 'unassigned-review' as const;

export interface UnassignedReviewBrowseState {
  kind: typeof UNASSIGNED_REVIEW_BROWSE_KIND;
  campaignId: string;
  taskIds: string[];
}

export interface UnassignedReviewBrowsePosition {
  index: number;
  total: number;
  previousTaskId: string | null;
  nextTaskId: string | null;
}

/**
 * Capture the currently visible unassigned-review pool in oldest-first order.
 * A snapshot makes previous/next stable while a reviewer is comparing tasks;
 * the claim command remains authoritative if another reviewer wins a race.
 */
export function createUnassignedReviewBrowseState(
  campaignId: string,
  tasks: Task[],
): UnassignedReviewBrowseState {
  const taskIds = sortReviewTasks(
    tasks.filter((task) => (
      task.campaignId === campaignId
      && task.status === 'in_review'
      && task.reviewerId === null
    )),
    'age',
  ).map((task) => task.id);

  return {
    kind: UNASSIGNED_REVIEW_BROWSE_KIND,
    campaignId,
    taskIds,
  };
}

/** Treat router state as untrusted input before using it for navigation. */
export function readUnassignedReviewBrowseState(
  value: unknown,
): UnassignedReviewBrowseState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (candidate.kind !== UNASSIGNED_REVIEW_BROWSE_KIND) return null;
  if (typeof candidate.campaignId !== 'string' || !candidate.campaignId.trim()) return null;
  if (!Array.isArray(candidate.taskIds) || candidate.taskIds.length === 0) return null;
  if (!candidate.taskIds.every((taskId) => typeof taskId === 'string' && taskId.trim())) return null;

  return {
    kind: UNASSIGNED_REVIEW_BROWSE_KIND,
    campaignId: candidate.campaignId.trim(),
    taskIds: candidate.taskIds.map((taskId) => (taskId as string).trim()),
  };
}

export function findUnassignedReviewBrowsePosition(
  state: UnassignedReviewBrowseState,
  taskId: string,
): UnassignedReviewBrowsePosition | null {
  const index = state.taskIds.indexOf(taskId);
  if (index < 0) return null;
  return {
    index,
    total: state.taskIds.length,
    previousTaskId: state.taskIds[index - 1] ?? null,
    nextTaskId: state.taskIds[index + 1] ?? null,
  };
}
